import React, { useEffect, useRef } from 'react';
import tw, { css } from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

interface GenericSidebarProps {
  title: string;
  icon: React.ReactNode; // Dynamic icon
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  footer?: React.ReactNode;
}

const GenericSidebar: React.FC<GenericSidebarProps> = ({
  title,
  icon,
  children,
  isOpen,
  onClose,
  footer,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close sidebar when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    // const handleClickOutside = (event: MouseEvent) => {
    //   if (
    //     sidebarRef.current &&
    //     !sidebarRef.current.contains(event.target as Node)
    //   ) {
    //     onClose();
    //   }
    // };

    // document.addEventListener('mousedown', handleClickOutside);

    // return () => {
    //   document.removeEventListener('mousedown', handleClickOutside);
    // };
  }, [isOpen, onClose]);

  useEffect(() => {
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (isOpen) {
      // Disable scroll on the body
      document.body.style.overflow = 'hidden';

      // Set theme color for sidebar overlay
      if (themeMeta) {
        themeMeta.setAttribute('content', '#5c8b86');
      }
    } else {
      // Restore scroll on the body
      document.body.style.overflow = '';

      // Reset theme color
      if (themeMeta) {
        themeMeta.setAttribute('content', '#adfff7'); // or your default theme color
      }
    }
  
    // Cleanup when component unmounts or when sidebar closes
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);  
  
  return (
    <div
      css={[
        tw`fixed inset-0 bg-black bg-opacity-50 z-50`,
        css`
          visibility: ${isOpen ? 'visible' : 'hidden'};
          opacity: ${isOpen ? '1' : '0'};
          transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
        `,
      ]}
    >
      <div
        ref={sidebarRef}
        css={[
          tw`fixed top-0 right-0 bg-white shadow-lg`,
          css`
            height: 100dvh;
            width: 96%;
            max-width: 418px;
            transform: translateX(${isOpen ? '0' : '100%'});
            transition: transform 0.3s ease-in-out;

            @media (min-width: 1024px) {
              width: 25rem; /* 400px */
            }
          `,
        ]}
        style={{
          paddingLeft: '1rem',
          paddingRight: '1rem',
          borderTopLeftRadius: '5px',
          borderBottomLeftRadius: '5px',
        }}
      >
        {/* Header */}
        <div
          tw="flex items-center justify-between mb-4 px-0 mx-0 mt-0"
          className="top-header-item"
          style={{
            paddingTop: '14px',
            paddingBottom: '14px',
            borderBottom: '2px solid #08364f',
            marginBottom: '12px'
          }}
        >
          <div tw="flex items-center">
            {/* <span className="d-flex mr-4 cart-icon">{icon}</span> */}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #D9D9D9',
                borderRadius: '3px',
                padding: '3px',
                marginRight: '10px',
                color: '#08364f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                transition: 'none',
                marginLeft: '8px'
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '24px', color: '#08364f', transform: 'scaleX(1.15)' }} />
            </button>
            <span className="heading" tw="text-lg font-semibold text-gray-900" style={{fontSize: "22.4px", fontWeight: '600'}}>
              {title}
            </span>
          </div>
          {/* <button
            tw="cursor-pointer text-gray-500 hover:text-gray-700"
            onClick={onClose}
            aria-label="Close"
            style={{ backgroundColor: 'transparent', transition: 'none' }}
          >
            <span className="close-icon d-flex cart-icon">
              <FontAwesomeIcon icon={faTimes} tw="h-6 w-6" />
            </span>
          </button> */}
        </div>

        {/* Content */}
        <div tw="flex flex-col h-full">
          {/* Scrollable Content Area */}
          <div
            css={css`
              flex-grow: 1;
              overflow-y: auto;
              max-height: calc(100dvh - 120px); /* Adjust based on header+footer */
              margin-right: -14px; /* shift scrollbar outside */
              padding-right: 30px;  /* reserve space so content doesn't get cut */
              position: fixed;
              height: 90dvh;
            `}
          >
            {children}
          </div>


          {/* Sticky Footer */}
          {footer && (
            <div
              tw="bg-white"
              css={{
                position: 'fixed',
                width: "calc(100% - 2rem)",
                bottom: 0,
                zIndex: 10,
                borderTop: '2px solid #B3B3B3',
                paddingTop: '12px',
                paddingBottom: '12px',
                display: 'block'
              }}
            >
              {footer}
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default GenericSidebar;
